import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import Breadcrumb from '../components/Breadcrumb';
import { personFileService } from '../services/api';
import PacketPreviewModal from '../components/PacketPreviewModal';
import {
  User, Mail, Phone, MapPin, FileText, Download, ShieldCheck, Send,
  CheckCircle2, ClipboardCheck, AlertTriangle, Building2, Bell, History,
  RefreshCw, PenSquare, Lock, Package, Upload,
} from 'lucide-react';

const OPS_FIELDS = [
  ['license_number', 'License Number'], ['license_state', 'License State'], ['license_class', 'License Class'],
  ['license_expiration', 'License Expiration'], ['endorsements', 'Endorsements'], ['restrictions', 'Restrictions'],
  ['medical_cert_expiration', 'DOT Medical Expiration'],
  ['vehicle_year', 'Vehicle Year'], ['vehicle_make', 'Vehicle Make'], ['vehicle_model', 'Vehicle Model'],
  ['vehicle_vin', 'VIN'], ['vehicle_plate', 'Plate'], ['vehicle_registration_state', 'Reg. State'],
  ['vehicle_registration_expiration', 'Reg. Expiration'], ['vehicle_gvwr', 'GVWR'],
  ['vehicle_payload', 'Payload'], ['vehicle_towing_capacity', 'Towing Capacity'],
  ['insurance_carrier', 'Insurance Carrier'], ['insurance_policy_number', 'Policy Number'],
  ['insurance_effective_date', 'Insurance Effective'], ['insurance_expiration_date', 'Insurance Expiration'],
  ['insurance_liability_limit', 'Liability Limit'],
];

const DOC_TYPE_OPTIONS = [
  ['vehicle_registration', 'Vehicle Registration'], ['vehicle_insurance', 'Certificate of Insurance'],
  ['drivers_license', "Driver's License"], ['cdl', 'CDL'], ['medical_cert', 'DOT Medical Certificate'],
  ['w9', 'W-9'], ['ic_agreement', 'Independent Contractor Agreement'], ['vehicle_photos', 'Vehicle Photographs'],
  ['vehicle_specs', 'Vehicle Specifications'], ['trailer_info', 'Trailer Information'],
  ['banking', 'Banking / Payment'], ['acknowledgments', 'Policy Acknowledgements'],
  ['mvr', 'MVR (internal)'], ['psp', 'PSP (internal)'], ['clearinghouse', 'Clearinghouse (internal)'],
  ['drug_alcohol', 'Drug & Alcohol (internal)'], ['other', 'Other'],
];

const STATUS_STYLE = {
  draft: 'bg-gray-100 text-gray-700',
  reviewed: 'bg-blue-100 text-blue-800',
  approved: 'bg-amber-100 text-amber-800',
  sent: 'bg-green-100 text-green-800',
};

const SECTION_TITLES = {
  SIGNABLE: 'Signable Documents',
  FORMS: 'Forms',
  APPLICANT_UPLOAD: 'Applicant Uploads',
  INTERNAL: 'Internal / Company-Supplied',
};

const PersonFilePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';

  const [data, setData] = useState(null);
  const [packet, setPacket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [newNote, setNewNote] = useState('');
  const [showSendModal, setShowSendModal] = useState(false);
  const [showPreviewPacket, setShowPreviewPacket] = useState(false);
  const [sendAuthorized, setSendAuthorized] = useState(false);
  const [editContact, setEditContact] = useState(false);
  const [cf, setCf] = useState({});
  const [showAddDoc, setShowAddDoc] = useState(false);
  const [docForm, setDocForm] = useState({ doc_key: 'vehicle_registration', file: null, expiration_date: '' });
  const [editOps, setEditOps] = useState(false);
  const [of, setOf] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pf, pk] = await Promise.all([
        personFileService.getPerson(id),
        personFileService.packetPreview(id).catch(() => null),
      ]);
      setData(pf);
      setPacket(pk);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to load person file');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(''), 4000); };

  const doDownload = async (docKey) => {
    try {
      const res = await personFileService.downloadDocument(id, docKey);
      window.open(res.download_url, '_blank');
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Download unavailable');
    }
  };

  const doVerify = async (docKey) => {
    setBusy(true); setErr('');
    try {
      await personFileService.verifyDocument(id, docKey, 'verify', 'Verified from Person File');
      await load();
      flash('Document marked VERIFIED.');
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Verify failed');
    } finally { setBusy(false); }
  };

  const submitDoc = async () => {
    if (!docForm.file) { setErr('Select a file to upload'); return; }
    setBusy(true); setErr('');
    try {
      const fd = new FormData();
      fd.append('doc_key', docForm.doc_key);
      if (docForm.expiration_date) fd.append('expiration_date', docForm.expiration_date);
      fd.append('file', docForm.file);
      await personFileService.addDocument(id, fd);
      setShowAddDoc(false);
      await load();
      flash('Document added — marked RECEIVED (verify separately).');
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Upload failed');
    } finally { setBusy(false); }
  };

  const saveOps = async () => {
    setBusy(true); setErr('');
    try {
      await personFileService.updatePerson(id, of);
      setEditOps(false);
      await load();
      flash('Operational details saved (admin-entered — not verified).');
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to save operational details');
    } finally { setBusy(false); }
  };

  const saveContact = async () => {
    if (cf.email && cf.email.trim() && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(cf.email.trim())) {
      setErr('Please enter a valid email address.'); return;
    }
    setBusy(true); setErr('');
    try {
      await personFileService.updatePerson(id, {
        first_name: cf.first_name, last_name: cf.last_name, email: cf.email,
        phone: cf.phone, city: cf.city, state: cf.state,
        address_line1: cf.address_line1, address_line2: cf.address_line2, zip: cf.zip,
      });
      setEditContact(false);
      await load();
      flash('Contact information updated — packet readiness re-evaluated.');
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to save contact information');
    } finally { setBusy(false); }
  };

  const doResend = async () => {
    setBusy(true); setErr('');
    try {
      const res = await personFileService.packetResend(id, 'Resent from Person File');
      await load();
      flash(res.email_sent ? 'Packet re-sent — document-request email delivered.' : `Resend attempted (email not delivered: ${res.email_reason || 'unknown'}).`);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to resend packet');
    } finally { setBusy(false); }
  };

  const handleNextAction = (cta) => {
    if (cta === 'edit_contact') {
      const pp = data.person;
      setCf({ first_name: pp.first_name || '', last_name: pp.last_name || '', email: pp.email || '', phone: pp.phone || '', city: pp.city || '', state: pp.state || '', address_line1: pp.address_line1 || '', address_line2: pp.address_line2 || '', zip: pp.zip || '' });
      setEditContact(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else if (cta === 'review_packet') { runPacketAction('review'); }
    else if (cta === 'approve_packet') { runPacketAction('approve'); }
    else if (cta === 'send_packet') { setShowSendModal(true); }
    else if (cta === 'retry_send' || cta === 'follow_up') { doResend(); }
    else if (cta === 'verify_documents' || cta === 'review') {
      document.querySelector('[data-testid="person-documents"]')?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const addNote = async () => {
    if (!newNote.trim()) return;
    setBusy(true);
    try {
      await personFileService.updatePerson(id, { note: newNote.trim() });
      setNewNote('');
      await load();
      flash('Note added');
    } finally { setBusy(false); }
  };

  const runPacketAction = async (action) => {
    setBusy(true); setErr('');
    try {
      if (action === 'review') await personFileService.packetReview(id, 'Reviewed from Person File');
      if (action === 'approve') await personFileService.packetApprove(id, 'Approved from Person File');
      await load();
      flash(`Packet ${action === 'review' ? 'marked as reviewed' : 'approved for sending'}`);
    } catch (e) {
      setErr(e?.response?.data?.detail || `Failed to ${action} packet`);
    } finally { setBusy(false); }
  };

  const doSend = async () => {
    setBusy(true); setErr('');
    try {
      const res = await personFileService.packetSend(id, true, true);
      setShowSendModal(false); setSendAuthorized(false);
      await load();
      flash(res.email_sent ? 'Packet sent — document-request email delivered.' : 'Packet marked sent (email not delivered).');
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to send packet');
    } finally { setBusy(false); }
  };

  if (loading) {
    return <DashboardLayout><div className="p-6 flex justify-center"><RefreshCw className="w-8 h-8 animate-spin text-gray-400" /></div></DashboardLayout>;
  }
  if (!data) {
    return <DashboardLayout><div className="p-6"><div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">{err || 'Person not found'}</div></div></DashboardLayout>;
  }

  const p = data.person;
  const ps = data.packet_state || {};
  const packetStatus = ps.status || 'draft';

  return (
    <DashboardLayout>
      <div className="p-6 max-w-6xl mx-auto" data-testid="person-file-page">
        <Breadcrumb items={[{ label: 'People', path: '/admin/people' }, { label: p.full_name }]} />

        {msg && <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-800 text-sm" data-testid="person-flash">{msg}</div>}
        {err && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm" data-testid="person-error">{err}</div>}

        {/* Header */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div className="flex items-start gap-4 min-w-0">
              <div className="w-14 h-14 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                <User className="w-7 h-7 text-blue-900" />
              </div>
              {!editContact ? (
              <div className="min-w-0">
                <h1 className="text-2xl font-bold text-gray-900 break-words" data-testid="person-name">{p.full_name}</h1>
                <div className="flex flex-wrap gap-x-5 gap-y-1 mt-2 text-sm text-gray-600">
                  {p.email && <span className="flex items-center gap-1 min-w-0"><Mail className="w-4 h-4 flex-shrink-0" /><span className="truncate">{p.email}</span></span>}
                  {p.phone && <span className="flex items-center gap-1"><Phone className="w-4 h-4" />{p.phone}</span>}
                  {[p.city, p.state].filter(Boolean).length > 0 && <span className="flex items-center gap-1"><MapPin className="w-4 h-4" />{[p.city, p.state].filter(Boolean).join(', ')}</span>}
                  {!p.email && !p.phone && ![p.city, p.state].filter(Boolean).length && <span className="text-gray-400 italic">No contact details on file</span>}
                </div>
                <div className="flex gap-2 mt-3 flex-wrap">
                  <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700">{p.application_type}</span>
                  <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">{p.status_label}</span>
                  <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">{p.record_source}</span>
                </div>
              </div>
              ) : (
                <div className="grid sm:grid-cols-2 gap-3 w-full max-w-xl" data-testid="contact-edit-form">
                  <EditField label="First Name" value={cf.first_name} onChange={(v) => setCf({ ...cf, first_name: v })} testid="edit-first-name" />
                  <EditField label="Last Name" value={cf.last_name} onChange={(v) => setCf({ ...cf, last_name: v })} testid="edit-last-name" />
                  <EditField label="Email" value={cf.email} onChange={(v) => setCf({ ...cf, email: v })} testid="edit-email" />
                  <EditField label="Phone" value={cf.phone} onChange={(v) => setCf({ ...cf, phone: v })} testid="edit-phone" />
                  <EditField label="City" value={cf.city} onChange={(v) => setCf({ ...cf, city: v })} testid="edit-city" />
                  <EditField label="State" value={cf.state} onChange={(v) => setCf({ ...cf, state: v })} testid="edit-state" />
                  <EditField label="Address Line 1" value={cf.address_line1} onChange={(v) => setCf({ ...cf, address_line1: v })} testid="edit-address1" />
                  <EditField label="Address Line 2" value={cf.address_line2} onChange={(v) => setCf({ ...cf, address_line2: v })} testid="edit-address2" />
                  <EditField label="ZIP" value={cf.zip} onChange={(v) => setCf({ ...cf, zip: v })} testid="edit-zip" />
                  <p className="sm:col-span-2 text-xs text-gray-400">Admin-entered contact details. Editing does not verify identity or documents.</p>
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              {!editContact ? (
                <button onClick={() => { setCf({ first_name: p.first_name || '', last_name: p.last_name || '', email: p.email || '', phone: p.phone || '', city: p.city || '', state: p.state || '', address_line1: p.address_line1 || '', address_line2: p.address_line2 || '', zip: p.zip || '' }); setEditContact(true); }}
                  data-testid="edit-contact-btn"
                  className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 text-sm text-gray-700">
                  <PenSquare className="w-4 h-4 text-gray-500" /> Edit Contact
                </button>
              ) : (
                <>
                  <button onClick={saveContact} disabled={busy} data-testid="save-contact-btn"
                    className="px-3 py-2 rounded-lg bg-blue-900 text-white hover:bg-blue-800 text-sm disabled:opacity-40">Save</button>
                  <button onClick={() => setEditContact(false)} data-testid="cancel-contact-btn"
                    className="px-3 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 text-sm text-gray-600">Cancel</button>
                </>
              )}
              {data.company && !editContact && (
                <Link to={`/admin/company/${data.company.id}`} data-testid="person-company-link"
                  className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 text-sm text-gray-700">
                  <Building2 className="w-4 h-4 text-gray-500" /> {data.company.name}
                </Link>
              )}
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6 min-w-0">
            {/* Operational Workflow — one place: Status → Readiness → Packet → Outstanding → Next Action */}
            {data.workflow && (() => {
              const wf = data.workflow;
              const na = wf.next_action || {};
              const del = wf.delivery || {};
              const out = wf.outstanding || {};
              const DELIVERY_LABEL = {
                sent_accepted: 'Provider accepted (HTTP 202) · delivery unknown', send_failed: 'Send FAILED',
                sent_unknown: 'Sent · result unknown', not_sent: 'Not sent',
                delivered: 'Delivered', deferred: 'Deferred (retrying)', bounced: 'Bounced',
                blocked: 'Blocked / dropped', spam_reported: 'Spam reported',
              };
              const DELIVERY_COLOR = {
                delivered: 'text-green-700', bounced: 'text-red-700', blocked: 'text-red-700',
                spam_reported: 'text-red-700', deferred: 'text-amber-700', send_failed: 'text-red-700',
              };
              const stageColor = wf.attention?.needed ? 'bg-amber-50 border-amber-200' : 'bg-white border-gray-200';
              return (
                <div className={`rounded-xl border p-6 ${stageColor}`} data-testid="person-workflow-panel">
                  <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                    <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2"><ClipboardCheck className="w-5 h-5 text-blue-900" /> Operational Status</h2>
                    <span className="px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800" data-testid="workflow-stage">{wf.stage_label}</span>
                  </div>

                  {wf.attention?.needed && wf.attention.reasons?.length > 0 && (
                    <div className="mb-4 rounded-lg bg-amber-100/60 border border-amber-200 p-3" data-testid="workflow-attention">
                      <p className="text-xs font-semibold text-amber-900 flex items-center gap-1 mb-1"><AlertTriangle className="w-3.5 h-3.5" /> Needs attention</p>
                      <ul className="text-xs text-amber-800 list-disc pl-4 space-y-0.5">
                        {wf.attention.reasons.map((r, i) => <li key={i}>{r}</li>)}
                      </ul>
                    </div>
                  )}

                  <div className="grid sm:grid-cols-2 gap-3 mb-4 text-sm">
                    <div className="rounded-lg border border-gray-100 bg-white p-3">
                      <p className="text-xs text-gray-400 mb-1">Readiness to send</p>
                      {wf.readiness?.ready_to_send
                        ? <p className="font-medium text-green-700 flex items-center gap-1"><CheckCircle2 className="w-4 h-4" /> Ready to send</p>
                        : <p className="font-medium text-red-700 flex items-center gap-1"><AlertTriangle className="w-4 h-4" /> Not ready</p>}
                      {!wf.readiness?.ready_to_send && (wf.readiness?.blockers || []).map((b, i) => <p key={i} className="text-xs text-gray-500 mt-0.5">{b}</p>)}
                    </div>
                    <div className="rounded-lg border border-gray-100 bg-white p-3">
                      <p className="text-xs text-gray-400 mb-1">Delivery</p>
                      <p className={`font-medium ${DELIVERY_COLOR[del.status] || 'text-gray-800'}`} data-testid="packet-delivery-status">{DELIVERY_LABEL[del.status] || del.status}</p>
                      {del.delivered_at && <p className="text-xs text-gray-500 mt-0.5">Delivered {new Date(del.delivered_at).toLocaleString()}</p>}
                      {del.delivery_reason && <p className="text-xs text-red-600 mt-0.5">{del.delivery_reason}</p>}
                      {del.sent_at && <p className="text-xs text-gray-500 mt-0.5">Sent {new Date(del.sent_at).toLocaleString()}</p>}
                      {(del.opened || del.clicked) && <p className="text-xs text-gray-500 mt-0.5">{del.opened ? 'Opened' : ''}{del.opened && del.clicked ? ' · ' : ''}{del.clicked ? 'Clicked' : ''} (≠ completed)</p>}
                      {wf.access?.accessed && <p className="text-xs text-green-700 mt-0.5">Recipient opened link{wf.access.accessed_at ? ` · ${new Date(wf.access.accessed_at).toLocaleDateString()}` : ''}</p>}
                    </div>
                  </div>

                  {(out.missing_required?.length > 0 || out.received_unverified?.length > 0 || out.expired?.length > 0) && (
                    <div className="mb-4 text-xs space-y-1" data-testid="workflow-outstanding">
                      {out.missing_required?.length > 0 && <p className="text-gray-600"><span className="font-semibold text-red-700">{out.missing_required.length} missing:</span> {out.missing_required.map((m) => m.label).join(', ')}</p>}
                      {out.received_unverified?.length > 0 && <p className="text-gray-600"><span className="font-semibold text-blue-700">{out.received_unverified.length} received · not verified:</span> {out.received_unverified.map((m) => m.label).join(', ')}</p>}
                      {out.expired?.length > 0 && <p className="text-gray-600"><span className="font-semibold text-red-700">{out.expired.length} expired:</span> {out.expired.map((m) => m.label).join(', ')}</p>}
                    </div>
                  )}

                  <div className="flex items-center justify-between gap-3 flex-wrap border-t border-gray-100 pt-4">
                    <div className="min-w-0">
                      <p className="text-xs text-gray-400">Next action</p>
                      <p className="text-sm font-medium text-gray-900" data-testid="workflow-next-action">{na.label}</p>
                      {na.detail && <p className="text-xs text-gray-500 mt-0.5">{na.detail}</p>}
                    </div>
                    {na.cta && na.cta !== 'none' && (
                      <button onClick={() => handleNextAction(na.cta)} disabled={busy}
                        data-testid="workflow-next-action-btn"
                        className="px-4 py-2 rounded-lg bg-blue-900 text-white text-sm font-medium hover:bg-blue-800 disabled:opacity-40 flex-shrink-0">
                        {na.label}
                      </button>
                    )}
                  </div>

                  {del.status === 'sent_accepted' && (
                    <p className="text-[11px] text-gray-400 mt-3">{del.note}</p>
                  )}
                </div>
              );
            })()}

            {/* Founder Packet */}
            <div className="bg-white rounded-xl border border-gray-200 p-6" data-testid="founder-packet-panel">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2"><Package className="w-5 h-5 text-blue-900" /> Founder Packet</h2>
                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${STATUS_STYLE[packetStatus]}`} data-testid="packet-status">{packetStatus.toUpperCase()}</span>
              </div>

              {packet && (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                    <Stat label="Required" value={packet.counts?.required} />
                    <Stat label="Received" value={packet.counts?.received} />
                    <Stat label="Conditional" value={packet.counts?.conditional} />
                    <Stat label="Legal Flags" value={packet.counts?.legal_flags} />
                  </div>

                  <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 mb-4 flex items-start gap-2" data-testid="esign-notice">
                    <Lock className="w-4 h-4 text-amber-600 mt-0.5" />
                    <p className="text-xs text-amber-800">
                      E-signatures are <strong>DISABLED</strong> (DocuSign production not configured). Sending dispatches a secure
                      document-request &amp; upload link only — no signature is collected.
                    </p>
                  </div>

                  {_SECTIONS.map((sec) => {
                    const items = (packet.sections?.[sec]) || [];
                    if (!items.length) return null;
                    return (
                      <div key={sec} className="mb-4" data-testid={`packet-section-${sec}`}>
                        <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">{SECTION_TITLES[sec]}</h3>
                        <div className="space-y-1.5">
                          {items.map((it) => (
                            <div key={it.key} className="flex items-center justify-between text-sm border border-gray-100 rounded-lg px-3 py-2 gap-3">
                              <div className="min-w-0 flex-1">
                                <p className="font-medium text-gray-800 truncate">{it.label}</p>
                                <p className="text-xs text-gray-400 truncate">{it.source_document}</p>
                              </div>
                              <div className="flex items-center gap-2 flex-shrink-0">
                                {it.legal_review && <span title="Legal review" className="text-amber-500"><ShieldCheck className="w-4 h-4" /></span>}
                                {it.verified ? (
                                  <span className="px-2 py-0.5 rounded text-xs font-medium bg-green-50 text-green-700 flex items-center gap-1" data-testid={`packet-badge-${it.key}`}><ShieldCheck className="w-3.5 h-3.5" /> VERIFIED</span>
                                ) : it.received ? (
                                  <span className="px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700" data-testid={`packet-badge-${it.key}`}>RECEIVED</span>
                                ) : (
                                  <span data-testid={`packet-badge-${it.key}`} className={`px-2 py-0.5 rounded text-xs font-medium ${
                                    it.status === 'REQUIRED' ? 'bg-red-50 text-red-700'
                                    : it.status === 'CONDITIONAL' || it.status === 'VERIFICATION_REQUIRED' ? 'bg-amber-50 text-amber-700'
                                    : 'bg-gray-50 text-gray-600'}`}>{it.status}</span>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </>
              )}

              {/* Workflow controls */}
              <div className="flex flex-wrap gap-3 mt-5 pt-5 border-t border-gray-100">
                <button onClick={() => setShowPreviewPacket(true)} disabled={busy}
                  data-testid="preview-packet-btn"
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-slate-800 text-white hover:bg-slate-700 disabled:opacity-40">
                  <Package className="w-4 h-4" /> Preview Packet
                </button>
                <button onClick={() => runPacketAction('review')} disabled={busy}
                  data-testid="packet-review-btn"
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-blue-50 text-blue-800 hover:bg-blue-100 disabled:opacity-40">
                  <ClipboardCheck className="w-4 h-4" /> Mark Reviewed
                </button>
                <button onClick={() => runPacketAction('approve')}
                  disabled={busy || !isSuperAdmin || !(packetStatus === 'reviewed')}
                  title={!isSuperAdmin ? 'Super Admin only' : ''}
                  data-testid="packet-approve-btn"
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-amber-50 text-amber-800 hover:bg-amber-100 disabled:opacity-40">
                  <CheckCircle2 className="w-4 h-4" /> Approve for Send
                </button>
                <button onClick={() => setShowSendModal(true)}
                  disabled={busy || !isSuperAdmin || !(packetStatus === 'approved')}
                  title={!isSuperAdmin ? 'Super Admin only' : ''}
                  data-testid="packet-send-btn"
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-green-600 text-white hover:bg-green-700 disabled:opacity-40">
                  <Send className="w-4 h-4" /> Send Packet
                </button>
                {packetStatus === 'sent' && (
                  <button onClick={doResend}
                    disabled={busy || !isSuperAdmin}
                    title={!isSuperAdmin ? 'Super Admin only' : 'Re-send the document-request email / follow up'}
                    data-testid="packet-resend-btn"
                    className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-slate-100 text-slate-700 hover:bg-slate-200 disabled:opacity-40">
                    <RefreshCw className="w-4 h-4" /> Resend / Follow up
                  </button>
                )}
              </div>
              {ps.sent_at && (
                <p className="text-xs text-gray-500 mt-3" data-testid="packet-sent-info">
                  Sent by {ps.sent_by} on {new Date(ps.sent_at).toLocaleString()} · e-signature disabled
                </p>
              )}
            </div>

            {/* Documents */}
            <div className="bg-white rounded-xl border border-gray-200 p-6" data-testid="person-documents">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2"><FileText className="w-5 h-5 text-blue-900" /> Documents</h2>
                <button onClick={() => { setDocForm({ doc_key: 'vehicle_registration', file: null, expiration_date: '' }); setShowAddDoc(true); }}
                  data-testid="add-document-btn"
                  className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-blue-800 bg-blue-50 rounded-lg hover:bg-blue-100">
                  <Upload className="w-4 h-4" /> Add Document
                </button>
              </div>
              {(!data.documents || data.documents.length === 0) ? (
                <p className="text-sm text-gray-500">No documents on file yet.</p>
              ) : (
                <div className="space-y-2">
                  {data.documents.map((d) => (
                    <div key={d.key} className="flex items-center justify-between border border-gray-100 rounded-lg px-3 py-2" data-testid={`document-row-${d.key}`}>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-800 truncate">{d.filename || d.key}</p>
                        <p className="text-xs text-gray-400 flex flex-wrap items-center gap-1.5">
                          <span>{d.key}</span>
                          {d.source && <span className="px-1.5 py-0.5 rounded bg-gray-100">{d.source === 'admin_upload' ? 'admin upload' : 'applicant upload'}</span>}
                          <span className={`px-1.5 py-0.5 rounded ${d.verified ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>{d.verified ? 'VERIFIED' : 'RECEIVED'}</span>
                          {d.status === 'superseded' && <span className="px-1.5 py-0.5 rounded bg-gray-100">superseded</span>}
                          {d.expiration_date && <span>exp {d.expiration_date}</span>}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {!d.verified && d.downloadable && (
                          <button onClick={() => doVerify(d.key)} data-testid={`document-verify-${d.key}`}
                            className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-green-800 bg-green-50 rounded-lg hover:bg-green-100">
                            <ShieldCheck className="w-3.5 h-3.5" /> Verify
                          </button>
                        )}
                        {d.downloadable ? (
                          <button onClick={() => doDownload(d.key)} data-testid={`document-download-${d.key}`}
                            className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-blue-800 bg-blue-50 rounded-lg hover:bg-blue-100">
                            <Download className="w-4 h-4" /> Download
                          </button>
                        ) : (
                          <span className="text-xs text-gray-400 italic">metadata only</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Operational Details (admin-entered) */}
            <div className="bg-white rounded-xl border border-gray-200 p-6" data-testid="person-operational">
              <div className="flex items-center justify-between mb-1">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2"><FileText className="w-5 h-5 text-blue-900" /> Operational Details</h2>
                {!editOps ? (
                  <button onClick={() => { setOf(p.operational || {}); setEditOps(true); }} data-testid="edit-ops-btn"
                    className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50"><PenSquare className="w-4 h-4 text-gray-500" /> Edit</button>
                ) : (
                  <div className="flex gap-2">
                    <button onClick={saveOps} disabled={busy} data-testid="save-ops-btn" className="px-3 py-1.5 rounded-lg bg-blue-900 text-white text-sm hover:bg-blue-800 disabled:opacity-40">Save</button>
                    <button onClick={() => setEditOps(false)} className="px-3 py-1.5 rounded-lg border border-gray-200 text-sm text-gray-600">Cancel</button>
                  </div>
                )}
              </div>
              <p className="text-xs text-gray-400 mb-4">Admin-entered values. Entering data here does NOT verify it — verification is a separate step.</p>
              {!editOps ? (
                <div className="grid sm:grid-cols-3 gap-x-6 gap-y-2 text-sm">
                  {OPS_FIELDS.map(([k, label]) => (
                    <div key={k}><span className="text-gray-400 text-xs block">{label}</span><span className="text-gray-800">{(p.operational || {})[k] || '—'}</span></div>
                  ))}
                </div>
              ) : (
                <div className="grid sm:grid-cols-3 gap-3" data-testid="ops-edit-form">
                  {OPS_FIELDS.map(([k, label]) => (
                    <EditField key={k} label={label} value={of[k] || ''} onChange={(v) => setOf({ ...of, [k]: v })} testid={`ops-${k}`} />
                  ))}
                </div>
              )}
            </div>

            {/* Notes */}
            <div className="bg-white rounded-xl border border-gray-200 p-6" data-testid="person-notes">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2"><PenSquare className="w-5 h-5 text-blue-900" /> Internal Notes</h2>
              <div className="flex gap-2 mb-4">
                <input value={newNote} onChange={(e) => setNewNote(e.target.value)} placeholder="Add an internal note…"
                  data-testid="person-note-input"
                  className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500" />
                <button onClick={addNote} disabled={busy || !newNote.trim()} data-testid="person-note-add-btn"
                  className="px-4 py-2 rounded-lg text-sm font-medium bg-blue-900 text-white hover:bg-blue-800 disabled:opacity-40">Add</button>
              </div>
              <div className="space-y-2 max-h-72 overflow-y-auto">
                {(data.internal_notes || []).slice().reverse().map((n, i) => (
                  <div key={i} className="text-sm border-l-2 border-gray-200 pl-3 py-1">
                    <p className="text-gray-800">{n.note}</p>
                    <p className="text-xs text-gray-400">{n.author} · {n.timestamp ? new Date(n.timestamp).toLocaleString() : ''}</p>
                  </div>
                ))}
                {(!data.internal_notes || data.internal_notes.length === 0) && <p className="text-sm text-gray-500">No notes yet.</p>}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6 min-w-0">
            <div className="bg-white rounded-xl border border-gray-200 p-6" data-testid="person-notifications">
              <h2 className="text-base font-semibold text-gray-900 mb-3 flex items-center gap-2"><Bell className="w-4 h-4 text-blue-900" /> Notifications</h2>
              {(data.notifications || []).length === 0 ? (
                <p className="text-sm text-gray-500">No linked notifications.</p>
              ) : data.notifications.map((n) => (
                <div key={n.id} className="text-sm py-2 border-b border-gray-100 last:border-0">
                  <p className="font-medium text-gray-800">{n.title}</p>
                  <p className="text-xs text-gray-500">{n.message}</p>
                </div>
              ))}
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-6" data-testid="person-audit">
              <h2 className="text-base font-semibold text-gray-900 mb-3 flex items-center gap-2"><History className="w-4 h-4 text-blue-900" /> Activity / Audit Trail</h2>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {(data.audit_trail || []).map((a, i) => (
                  <div key={i} className="text-xs border-l-2 border-gray-200 pl-3 py-1">
                    <p className="font-medium text-gray-700">{a.action}</p>
                    <p className="text-gray-400">{a.details?.actor_email || a.user_id} · {a.timestamp ? new Date(a.timestamp).toLocaleString() : ''}</p>
                  </div>
                ))}
                {(!data.audit_trail || data.audit_trail.length === 0) && <p className="text-sm text-gray-500">No activity yet.</p>}
              </div>
            </div>
          </div>
        </div>
      </div>

      {showPreviewPacket && (
        <PacketPreviewModal personId={id} onClose={() => setShowPreviewPacket(false)} />
      )}

      {showAddDoc && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="add-doc-modal">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-1">Add Document</h3>
            <p className="text-xs text-gray-500 mb-4">Attach a document received outside the portal. It will be marked <strong>RECEIVED</strong> (not verified) and mapped to the matching requirement.</p>
            <label className="text-sm text-gray-600">Document type</label>
            <select value={docForm.doc_key} onChange={(e) => setDocForm({ ...docForm, doc_key: e.target.value })}
              data-testid="add-doc-type" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mb-3">
              {DOC_TYPE_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <label className="text-sm text-gray-600">Expiration date (optional)</label>
            <input type="date" value={docForm.expiration_date} onChange={(e) => setDocForm({ ...docForm, expiration_date: e.target.value })}
              data-testid="add-doc-expiration" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mb-3" />
            <label className="text-sm text-gray-600">File</label>
            <input type="file" onChange={(e) => setDocForm({ ...docForm, file: e.target.files?.[0] || null })}
              data-testid="add-doc-file" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mb-4" />
            <div className="flex justify-end gap-3">
              <button onClick={() => setShowAddDoc(false)} className="px-4 py-2 rounded-lg text-sm text-gray-600 hover:bg-gray-100">Cancel</button>
              <button onClick={submitDoc} disabled={busy || !docForm.file} data-testid="add-doc-submit"
                className="px-4 py-2 rounded-lg text-sm font-medium bg-blue-900 text-white hover:bg-blue-800 disabled:opacity-40">
                {busy ? 'Uploading…' : 'Upload'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Send authorization modal */}
      {showSendModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="send-modal">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2"><Send className="w-5 h-5 text-green-600" /> Send Onboarding Packet</h3>
            <p className="text-sm text-gray-600 mb-4">
              This dispatches a secure document-request email &amp; upload link to <strong>{p.full_name}</strong> ({p.email || 'no email'}).
              No e-signature is collected. This action is audited.
            </p>
            <label className="flex items-start gap-2 mb-4 text-sm text-gray-700">
              <input type="checkbox" checked={sendAuthorized} onChange={(e) => setSendAuthorized(e.target.checked)}
                data-testid="send-authorize-checkbox" className="mt-0.5" />
              I authorize sending this packet now.
            </label>
            <div className="flex justify-end gap-3">
              <button onClick={() => { setShowSendModal(false); setSendAuthorized(false); }} data-testid="send-cancel-btn"
                className="px-4 py-2 rounded-lg text-sm text-gray-600 hover:bg-gray-100">Cancel</button>
              <button onClick={doSend} disabled={!sendAuthorized || busy} data-testid="send-confirm-btn"
                className="px-4 py-2 rounded-lg text-sm font-medium bg-green-600 text-white hover:bg-green-700 disabled:opacity-40">
                {busy ? 'Sending…' : 'Confirm Send'}
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

const _SECTIONS = ['SIGNABLE', 'FORMS', 'APPLICANT_UPLOAD', 'INTERNAL'];

const EditField = ({ label, value, onChange, testid }) => (
  <div>
    <label className="text-xs text-gray-500">{label}</label>
    <input value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500" />
  </div>
);

const Stat = ({ label, value }) => (
  <div className="rounded-lg bg-gray-50 border border-gray-100 p-3 text-center">
    <p className="text-2xl font-bold text-gray-900">{value ?? 0}</p>
    <p className="text-xs text-gray-500">{label}</p>
  </div>
);

export default PersonFilePage;
